import { useEffect, useState } from "react";
import { X, MessageSquareText, Loader2 } from "lucide-react";
import { Telemetria } from "./../types/types";
import styles from "./TelemetriaModal.module.css";

const API_URL = process.env.REACT_APP_API || "https://localhost:5001/api";

interface Props {
  id: number;
  onClose: () => void;
}

export default function TelemetriaDetailsModal({ id, onClose }: Props) {
  const [telemetria, setTelemetria] = useState<Telemetria | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/Telemetria/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Erro ${res.status} ao buscar telemetria`);
        return res.json();
      })
      .then(setTelemetria)
      .catch((err) => console.error("Erro ao carregar telemetria", err))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>
          <X size={18} />
        </button>

        {loading || !telemetria ? (
          <div className={styles.loadingState}>
            <Loader2 size={22} className={styles.spinner} />
            Carregando dados...
          </div>
        ) : (
          <>
            <span className={styles.eyebrow}>Ficha da telemetria</span>
            <h2 className={styles.modalTitle}>{telemetria.identificacao}</h2>

            <div className={styles.summaryGrid}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Endereço</span>
                <span className={styles.summaryValue}>
                  {telemetria.endereco}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Contato</span>
                <span className={styles.summaryValue}>
                  {telemetria.contato || "—"}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Patrimônio Ikone</span>
                <span className={styles.summaryValue}>
                  {telemetria.patrimonioIkone || "—"}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Operadora</span>
                <span className={styles.summaryValue}>
                  {telemetria.operadora || "—"}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Informações</span>
                <span className={styles.summaryValue}>
                  {telemetria.informacoes || "—"}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Qtd. de centrais</span>
                <span className={styles.summaryValue}>
                  {telemetria.centrais.length}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Responsável</span>
                <span className={styles.summaryValue}>
                  {telemetria.responsavel || "—"}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Data da instalação</span>
                <span className={styles.summaryValue}>
                  {telemetria.dataInstalacao
                    ? new Date(telemetria.dataInstalacao).toLocaleDateString(
                        "pt-BR",
                        { timeZone: "UTC" },
                      )
                    : "—"}
                </span>
              </div>
            </div>

            {telemetria.centrais.map((central) => (
              <div key={central.id} className={styles.centralBlock}>
                <div className={styles.centralHeader}>
                  <span className={styles.centralBadge}>
                    {central.idCentral}
                  </span>
                  <span className={styles.centralLabel}>ID Central</span>
                </div>

                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Entrada</th>
                        <th>Dispositivo</th>
                        <th>Marca/Modelo</th>
                        <th>Adicional</th>
                        <th>Info</th>
                        <th>Local instalação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {central.entradas
                        .filter((e) => e.dispositivo)
                        .map((entrada) => (
                          <tr key={entrada.id ?? entrada.numero}>
                            <td>
                              <span className={styles.entradaTag}>
                                {entrada.numero}
                                {entrada.tipo}
                              </span>
                            </td>
                            <td>{entrada.dispositivo || "—"}</td>
                            <td>{entrada.marcaModelo || "—"}</td>
                            <td>{entrada.adicional || "—"}</td>
                            <td>{entrada.info || "—"}</td>
                            <td>
                              {entrada.localInstalacao || "—"}
                              {entrada.comentario && (
                                <div
                                  className={styles.commentFlag}
                                  title={entrada.comentario}
                                >
                                  <MessageSquareText size={13} />
                                  {entrada.comentario}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
